import { useState } from "react";
import axios from "axios";
import { Send, Check } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATINGS = [
  { value: 1, emoji: "😞", label: "Poor" },
  { value: 2, emoji: "😐", label: "Okay" },
  { value: 3, emoji: "🙂", label: "Good" },
  { value: 4, emoji: "😄", label: "Great" },
  { value: 5, emoji: "🤩", label: "Amazing" },
];

export default function Feedback() {
  const [rating, setRating] = useState(null);
  const [highlight, setHighlight] = useState("");
  const [improve, setImprove] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async () => {
    if (!rating) {
      toast.error("Please select a rating");
      return;
    }
    setSubmitting(true);
    try {
      const comments = `Highlight: ${highlight || "—"}\n\nCould be better: ${improve || "—"}`;
      await axios.post(`${API}/feedback`, { rating, comments });
      setSubmitted(true);
    } catch (err) {
      toast.error("Could not send feedback. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{
          backgroundColor: "var(--background)",
          padding: "32px",
          minHeight: "calc(100vh - 112px)",
        }}
        data-testid="feedback-thanks"
      >
        <div
          className="flex items-center justify-center"
          style={{
            width: "96px",
            height: "96px",
            borderRadius: "48px",
            backgroundColor: "var(--primary)",
            marginBottom: "24px",
          }}
        >
          <Check size={48} color="white" strokeWidth={3} />
        </div>
        <p
          style={{
            fontSize: "28px",
            fontWeight: 800,
            color: "var(--primary-dark)",
            marginBottom: "12px",
          }}
        >
          Thank You!
        </p>
        <p
          style={{
            color: "var(--text-muted)",
            fontSize: "15px",
            textAlign: "center",
            lineHeight: "22px",
            marginBottom: "24px",
          }}
        >
          Your feedback fuels our growth. We appreciate you taking the time to
          share your thoughts today.
        </p>
        <p
          style={{
            color: "var(--primary)",
            fontWeight: 800,
            letterSpacing: "1.5px",
            fontSize: "13px",
          }}
        >
          Success Starts Here
        </p>
      </div>
    );
  }

  return (
    <div style={{ padding: "16px", paddingBottom: "40px" }} data-testid="feedback-page">
      <h2
        style={{
          fontSize: "22px",
          fontWeight: 800,
          color: "var(--primary-dark)",
          marginBottom: "4px",
        }}
      >
        Share Your Feedback
      </h2>
      <p
        style={{
          color: "var(--text-muted)",
          fontSize: "14px",
          marginBottom: "20px",
        }}
      >
        A quick evaluation helps us plan even better events.
      </p>

      <p
        style={{
          fontSize: "14px",
          fontWeight: 700,
          color: "var(--primary-dark)",
          marginBottom: "10px",
          marginTop: "8px",
        }}
      >
        How was today?
      </p>

      <div
        className="flex justify-between"
        style={{ marginBottom: "16px", gap: "6px" }}
        data-testid="feedback-rating"
      >
        {RATINGS.map((r) => {
          const active = rating === r.value;
          return (
            <button
              key={r.value}
              type="button"
              onClick={() => setRating(r.value)}
              data-testid={`rating-${r.value}`}
              className="flex-1 flex flex-col items-center"
              style={{
                paddingTop: "12px",
                paddingBottom: "12px",
                backgroundColor: active ? "#FDF4DC" : "var(--surface)",
                borderRadius: "12px",
                borderWidth: "2px",
                borderStyle: "solid",
                borderColor: active ? "var(--gold)" : "var(--border)",
              }}
            >
              <span style={{ fontSize: "28px" }}>{r.emoji}</span>
              <span
                style={{
                  fontSize: "10px",
                  fontWeight: 600,
                  color: active ? "var(--primary-dark)" : "var(--text-muted)",
                  marginTop: "4px",
                }}
              >
                {r.label}
              </span>
            </button>
          );
        })}
      </div>

      <p
        style={{
          fontSize: "14px",
          fontWeight: 700,
          color: "var(--primary-dark)",
          marginBottom: "10px",
          marginTop: "8px",
        }}
      >
        What was the highlight?
      </p>
      <textarea
        data-testid="feedback-highlight"
        value={highlight}
        onChange={(e) => setHighlight(e.target.value)}
        placeholder="A favorite session, takeaway, or moment..."
        rows={4}
        className="w-full resize-none"
        style={{
          backgroundColor: "var(--surface)",
          borderRadius: "12px",
          border: "1px solid var(--border)",
          padding: "14px",
          fontSize: "14px",
          color: "var(--primary-dark)",
          marginBottom: "8px",
          outline: "none",
        }}
      />

      <p
        style={{
          fontSize: "14px",
          fontWeight: 700,
          color: "var(--primary-dark)",
          marginBottom: "10px",
          marginTop: "8px",
        }}
      >
        What could be even better?
      </p>
      <textarea
        data-testid="feedback-improve"
        value={improve}
        onChange={(e) => setImprove(e.target.value)}
        placeholder="Suggestions, ideas, or wishes..."
        rows={4}
        className="w-full resize-none"
        style={{
          backgroundColor: "var(--surface)",
          borderRadius: "12px",
          border: "1px solid var(--border)",
          padding: "14px",
          fontSize: "14px",
          color: "var(--primary-dark)",
          marginBottom: "8px",
          outline: "none",
        }}
      />

      <button
        type="button"
        onClick={submit}
        disabled={!rating || submitting}
        data-testid="feedback-submit"
        className="w-full flex items-center justify-center"
        style={{
          backgroundColor: "var(--gold)",
          paddingTop: "16px",
          paddingBottom: "16px",
          borderRadius: "14px",
          gap: "8px",
          marginTop: "20px",
          opacity: !rating || submitting ? 0.6 : 1,
        }}
      >
        <Send size={18} color="var(--primary-dark)" />
        <span
          style={{
            color: "var(--primary-dark)",
            fontWeight: 800,
            fontSize: "16px",
          }}
        >
          {submitting ? "Submitting..." : "Submit Feedback"}
        </span>
      </button>
    </div>
  );
}
